import React from 'react';
import { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { isPlanErrorMessage } from '../lib/api';

/* ----------------------------- Card ----------------------------- */
export function Card({
  className = '',
  padded = false,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & { padded?: boolean }) {
  return (
    <div
      className={`bg-surface border border-line rounded-[15px] shadow-card-sm ${padded ? 'p-5' : ''} ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

/* -------------------------- PageHeader -------------------------- */
export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="flex items-end justify-between gap-4 flex-wrap mb-5">
      <div>
        {eyebrow && <div className="ds-eyebrow">{eyebrow}</div>}
        <h2 className="m-0 mt-1.5 font-display font-bold text-[24px] text-ink tracking-tight">{title}</h2>
        {description && <p className="m-0 mt-1.5 font-medium text-[13px] text-ink-2">{description}</p>}
      </div>
      {actions && <div className="flex gap-2.5 flex-wrap">{actions}</div>}
    </div>
  );
}

/* ---------------------------- Button ---------------------------- */
type BtnVariant = 'primary' | 'ghost' | 'danger';

export function Button({
  variant = 'primary',
  icon: Icon,
  className = '',
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: BtnVariant; icon?: LucideIcon }) {
  const base = variant === 'primary' ? 'ds-btn-primary' : variant === 'danger' ? 'ds-btn-danger' : 'ds-btn-ghost';
  return (
    <button className={`ds-btn ${base} ${className}`} {...rest}>
      {Icon && <Icon className="w-4 h-4" />}
      {children}
    </button>
  );
}

/* ----------------------------- Badge ---------------------------- */
type BadgeTone = 'success' | 'danger' | 'warning' | 'accent' | 'neutral';

export function Badge({
  tone = 'neutral',
  dot = false,
  pulse = false,
  className = '',
  children,
}: {
  tone?: BadgeTone;
  dot?: boolean;
  pulse?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  const toneClass = {
    success: 'ds-badge-success',
    danger: 'ds-badge-danger',
    warning: 'ds-badge-warning',
    accent: 'ds-badge-accent',
    neutral: 'ds-badge-neutral',
  }[tone];
  const dotColor = {
    success: 'var(--success)', danger: 'var(--danger)', warning: 'var(--warning)',
    accent: 'var(--accent-ink)', neutral: 'var(--ink-3)',
  }[tone];
  return (
    <span className={`ds-badge ${toneClass} ${className}`}>
      {dot && (
        <span
          className={`w-1.5 h-1.5 rounded-full ${pulse ? 'animate-mlspulse' : ''}`}
          style={{ background: dotColor }}
        />
      )}
      {children}
    </span>
  );
}

/* --------------------------- StatCard --------------------------- */
type StatTone = 'accent' | 'cyan' | 'violet' | 'amber';

const STAT_TONE: Record<StatTone, { bg: string; fg: string }> = {
  accent: { bg: 'var(--accent-soft)', fg: 'var(--accent-ink)' },
  cyan: { bg: 'var(--cyan-soft)', fg: 'var(--cyan-ink)' },
  violet: { bg: 'var(--violet-soft)', fg: 'var(--violet)' },
  amber: { bg: 'var(--aqua-soft)', fg: 'var(--aqua-ink)' },
};

export function StatCard({
  label, value, delta, icon: Icon, tone = 'accent',
}: {
  label: string;
  value: React.ReactNode;
  delta?: string;
  icon: LucideIcon;
  tone?: StatTone;
}) {
  const t = STAT_TONE[tone];
  return (
    <div className="bg-surface border border-line rounded-[15px] p-5 shadow-card-sm transition-all hover:-translate-y-0.5 hover:shadow-card">
      <div className="flex items-center justify-between">
        <span className="font-semibold text-[12.5px] text-ink-2">{label}</span>
        <span
          className="flex items-center justify-center w-[38px] h-[38px] rounded-[11px]"
          style={{ background: t.bg, color: t.fg }}
        >
          <Icon className="w-[18px] h-[18px]" />
        </span>
      </div>
      <div className="font-display font-bold text-[32px] text-ink tracking-tight mt-3">{value}</div>
      {delta && <div className="font-semibold text-[11.5px] text-ink-3 mt-0.5">{delta}</div>}
    </div>
  );
}

/* --------------------------- EmptyState ------------------------- */
export function EmptyState({
  icon: Icon, title, description, action,
}: {
  icon: LucideIcon;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center text-center min-h-[50vh] animate-fade">
      <div className="w-[72px] h-[72px] rounded-[20px] bg-accent-soft text-accent-ink flex items-center justify-center mb-[18px]">
        <Icon className="w-7 h-7" />
      </div>
      <h2 className="m-0 font-display font-bold text-[22px] text-ink">{title}</h2>
      {description && <p className="mt-2 mb-5 max-w-[380px] font-medium text-[14px] text-ink-2 leading-relaxed">{description}</p>}
      {action}
    </div>
  );
}

/* ---------------------------- Spinner --------------------------- */
export function Spinner({ className = '' }: { className?: string }) {
  return <div className={`animate-spin rounded-full h-8 w-8 border-2 border-line border-t-accent ${className}`} />;
}

/* ------------------------- ErrorMessage ------------------------- */
/**
 * Renders an API error string. When the message came from a 402 plan error
 * (plan_limit / plan_required / subscription_required) it appends a link to /billing.
 */
export function ErrorMessage({ message }: { message: string | null | undefined }) {
  if (!message) return null;
  return (
    <span>
      {message}
      {isPlanErrorMessage(message) && (
        <>
          {' '}
          <Link to="/billing" className="font-semibold underline underline-offset-2 hover:opacity-80">
            Upgrade under Billing
          </Link>
        </>
      )}
    </span>
  );
}
